import {
  differenceInDays,
  format,
  formatISO,
  isValid,
  parse,
  parseISO,
  startOfDay,
  subYears,
} from 'date-fns';
import { localeMappings } from '@/lib/locales';
import { enUS } from 'date-fns/locale/en-US';
import { DATE_LONG_FORMAT, STANDARD_DATE_FORMAT } from '@/configs';

export { differenceInDays };

export function monthsToYearsAndMonths(months: number): string {
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  const yearsLabel = years > 1 ? 'years' : 'year';
  const monthsLabel =
    remainingMonths === 0 || remainingMonths > 1 ? 'months' : 'month';

  if (years === 0) {
    return `${remainingMonths} ${monthsLabel}`;
  }

  if (remainingMonths === 0) {
    return `${years} ${yearsLabel}`;
  }

  return `${years} ${yearsLabel} ${remainingMonths} ${monthsLabel}`;
}

export function parseUtcIsoDate(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const normalized = hasTimezone ? trimmed : `${trimmed}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatUtcIsoDateTimeLocal(
  value?: string | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) return '';
  const date = parseUtcIsoDate(value);
  if (!date) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  });
}

export function formatDateISOString(dateString: string): string {
  const date = parseISO(dateString);
  return isValid(date) ? format(date, STANDARD_DATE_FORMAT) : '';
}

export function formatDob(dob: string | null | undefined): string {
  if (!dob) return '—';
  const date = parseISO(dob);
  return isValid(date) ? format(date, STANDARD_DATE_FORMAT) : dob;
}

export function formatDate(date: Date): string {
  return isValid(date) ? format(date, STANDARD_DATE_FORMAT) : '';
}

export function ISODateStringToDate(
  isoDateString: string | undefined,
): Date | undefined {
  if (!isoDateString) return undefined;
  const date = parseISO(isoDateString);
  return isValid(date) ? date : undefined;
}

export function toISODateString(date: string | number | Date): string {
  const parsed = new Date(date);
  return isValid(parsed) ? formatISO(parsed, { representation: 'date' }) : '';
}

export function isValidDate(date: any): boolean {
  if (!date) return false;
  return isValid(new Date(date));
}

export function formatDateWithShortMonth(
  dateString: string,
  localeKey: string,
): string {
  const date = parseISO(dateString);
  if (!isValid(date)) return '';

  const locale = localeMappings[localeKey] || enUS;
  return format(date, DATE_LONG_FORMAT, { locale });
}

export const isValidData = (date: any) => {
  return date && isValid(new Date(date));
};

export const dateWithoutTime = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

/**
 * Rolling minimum valid calendar date for animal sanity date rules, aligned with
 * backend `RuleEvaluationService.GetRidiculouslyOldCutoffDate` (local today at
 * midnight minus 150 years).
 */
export function getRidiculouslyOldCutoffDate(): Date {
  return startOfDay(subYears(new Date(), 150));
}

/** MUI X v8 sectioned date field root (PickersSectionList). */
const PICKERS_SECTION_LIST_ROOT = '.MuiPickersSectionList-root';

/**
 * Canonical message shown for any "user has not produced a valid date" state
 * on a required date field — empty, partially typed, or impossible-date
 * (e.g. `00/00/0000`). Used by both client-side validation paths (blur and
 * Zod) and is intended to take precedence over server-supplied messages
 * (e.g. "Date of Birth is required") so the experience is consistent.
 */
export const INVALID_DATE_MESSAGE = 'Please enter a valid date.';

/**
 * Visible text from a MUI PickersTextField (section list). Used on blur when
 * the committed value can stay `null` while the user typed invalid/partial input.
 */
export function getDisplayStringFromPickerFormControl(
  formControlEl: HTMLElement,
): string {
  const root = formControlEl.querySelector(PICKERS_SECTION_LIST_ROOT);
  if (!root) return '';
  return (root.textContent ?? '').replace(/\u00a0/g, ' ').trim();
}

export type PickerDisplayBlurResult =
  | { kind: 'empty' }
  | { kind: 'invalid' }
  | { kind: 'minDate' }
  | { kind: 'maxDate' }
  | { kind: 'valid' };

export type PickerDisplayBlurBounds = {
  minDate?: Date;
  maxDate?: Date;
};

/**
 * Validate what the user sees in the picker field vs `STANDARD_DATE_FORMAT`
 * (`dd/MM/yyyy`). If there are no digits, treat as empty (placeholders only).
 */
export function validatePickerDisplayOnBlur(
  formControlEl: HTMLElement,
  bounds?: PickerDisplayBlurBounds,
): PickerDisplayBlurResult {
  const display = getDisplayStringFromPickerFormControl(formControlEl);
  if (!/\d/.test(display)) {
    return { kind: 'empty' };
  }
  const parsed = parse(display, STANDARD_DATE_FORMAT, new Date());
  if (!isValid(parsed)) {
    return { kind: 'invalid' };
  }
  const day = dateWithoutTime(parsed);
  if (bounds?.minDate && day < dateWithoutTime(bounds.minDate)) {
    return { kind: 'minDate' };
  }
  if (bounds?.maxDate && day > dateWithoutTime(bounds.maxDate)) {
    return { kind: 'maxDate' };
  }
  return { kind: 'valid' };
}
